// frontend/src/components/masters/VendorList.js
import React, { useState, useEffect } from 'react';
import VendorForm from './VendorForm';
import useSortableTable from '../common/useSortableTable';
import './VendorList.css';
import { usePermissions } from '../../services/PermissionsContext'; // 1. Import

// Placeholder API endpoint - replace with your actual Django API endpoint for Vendors
const API_BASE_URL = '/api/masters/vendors/'; // Added trailing slash

// Helper to get auth token
const getAuthHeaders = () => ({
    'Authorization': `Bearer ${localStorage.getItem('kk_token')}`,
    'Content-Type': 'application/json'
});

const VendorList = () => {
    const [vendors, setVendors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentVendor, setCurrentVendor] = useState(null);
    const [formMode, setFormMode] = useState('add'); // 'add', 'edit', 'view'
    const [searchTerm, setSearchTerm] = useState('');

    const { hasPermission } = usePermissions(); // 2. Get hook

    // Fetch data from Django backend
    const fetchVendors = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(API_BASE_URL, {
                headers: getAuthHeaders()
            });
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || 'Failed to fetch vendors');
            }
            const data = await response.json();
            // Assign a unique temporary ID if not already present, useful for lists before saving
            const processedData = data.map(item => ({ ...item, id: item.id || crypto.randomUUID() }));
            setVendors(processedData);
        } catch (err) {
            console.error("Error fetching vendors:", err);
            setError("Failed to load vendors: " + err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchVendors();
    }, []);

    const handleAddVendor = () => {
        setCurrentVendor(null); // Clear any previous data
        setFormMode('add');
        setIsModalOpen(true);
    };

    const handleEditVendor = (vendor) => {
        setCurrentVendor(vendor);
        setFormMode('edit');
        setIsModalOpen(true);
    };

    const handleViewVendor = (vendor) => {
        setCurrentVendor(vendor);
        setFormMode('view');
        setIsModalOpen(true);
    };

    const handleDeleteVendor = async (id) => {
        if (window.confirm("Are you sure you want to delete this vendor?")) { // Use custom modal in real app
            try {
                const response = await fetch(`${API_BASE_URL}${id}/`, {
                    method: 'DELETE',
                    headers: getAuthHeaders()
                });
                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.detail || 'Failed to delete vendor');
                }
                setVendors(prev => prev.filter(v => v.id !== id));
                // alert("Vendor deleted successfully!"); // Use custom modal for success
            } catch (err) {
                console.error("Error deleting vendor:", err);
                setError("Failed to delete vendor.");
            }
        }
    };

    const handleFormClose = (refresh = false) => {
        setIsModalOpen(false);
        setCurrentVendor(null); // Clear current vendor data
        if (refresh) {
            fetchVendors(); // Re-fetch data if a save operation occurred
        }
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    const filteredVendors = vendors
        .filter(vendor =>
            Object.values(vendor).some(value =>
                String(value).toLowerCase().includes(searchTerm.toLowerCase())
            )
        );

    const { items: sortedAndFilteredVendors, requestSort, getSortIndicator } = useSortableTable(filteredVendors);

    return (
        <div className="vendor-list-container">
            <h2>Vendors List</h2>

            {loading && <p className="loading-message">Loading vendors...</p>}
            {error && <p className="error-message">{error}</p>}

            <div className="toolbar">
                <input
                    type="text"
                    placeholder="Search vendors..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                />
                {/* 3. Protect Add button */}
                {hasPermission('VENDORS', 'can_create') && (
                    <button onClick={handleAddVendor}>Add New Vendor</button>
                )}
            </div>

            {!loading && !error && (
                sortedAndFilteredVendors.length > 0 ? (
                    <table>
                        <thead>
                            <tr>
                                <th onClick={() => requestSort('name')} className="sortable">
                                    Name {getSortIndicator('name')}
                                </th>
                                <th onClick={() => requestSort('vendor_type')} className="sortable">
                                    Type {getSortIndicator('vendor_type')}
                                </th>
                                <th onClick={() => requestSort('phone')} className="sortable">
                                    Phone {getSortIndicator('phone')}
                                </th>
                                <th onClick={() => requestSort('address')} className="sortable">
                                    Address {getSortIndicator('address')}
                                </th>
                                <th onClick={() => requestSort('city')} className="sortable">
                                    City {getSortIndicator('city')}
                                </th>
                                <th onClick={() => requestSort('tin')} className="sortable">
                                    TIN {getSortIndicator('tin')}
                                </th>
                                <th onClick={() => requestSort('vrn')} className="sortable">
                                    VRN {getSortIndicator('vrn')}
                                </th>
                                {/* 4. Protect Actions column header */}
                                {(hasPermission('VENDORS', 'can_view') || hasPermission('VENDORS', 'can_edit') || hasPermission('VENDORS', 'can_delete')) && (
                                    <th>Actions</th>
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            {sortedAndFilteredVendors.map(vendor => (
                                <tr key={vendor.id}>
                                    <td>{String(vendor.name || '').toUpperCase()}</td>
                                    <td>{String(vendor.vendor_type || '').toUpperCase()}</td>
                                    <td>{String(vendor.phone || '').toUpperCase()}</td>
                                    <td>{String(vendor.address || '').toUpperCase()}</td>
                                    <td>{String(vendor.city || '').toUpperCase()}</td>
                                    <td>{String(vendor.tin || '').toUpperCase()}</td>
                                    <td>{String(vendor.vrn || '').toUpperCase()}</td>
                                    {/* 5. Protect Actions cell */}
                                    {(hasPermission('VENDORS', 'can_view') || hasPermission('VENDORS', 'can_edit') || hasPermission('VENDORS', 'can_delete')) && (
                                        <td className="actions">
                                            {hasPermission('VENDORS', 'can_view') && (
                                                <button className="icon-btn view-btn" onClick={() => handleViewVendor(vendor)} title="View">
                                                    <i className="fa fa-eye"></i>
                                                </button>
                                            )}
                                            {hasPermission('VENDORS', 'can_edit') && (
                                                <button className="icon-btn edit-btn" onClick={() => handleEditVendor(vendor)} title="Edit">
                                                    <i className="fa fa-pencil-alt"></i>
                                                </button>
                                            )}
                                            {hasPermission('VENDORS', 'can_delete') && (
                                                <button className="icon-btn delete-btn" onClick={() => handleDeleteVendor(vendor.id)} title="Delete">
                                                    <i className="fa fa-trash"></i>
                                                </button>
                                            )}
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                ) : (
                    <p className="no-data">No vendors found.</p>
                )
            )}

            {isModalOpen && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <button className="modal-close-btn" onClick={() => handleFormClose(false)}>
                            &times;
                        </button>
                        <VendorForm
                            mode={formMode}
                            initialData={currentVendor}
                            onClose={handleFormClose}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default VendorList;
